/**
 * Chores & Events chatbot feature — isolated from other chatbot flows.
 * Flip CHORES_EVENTS_ENABLED to false to disable without removing integration.
 */

export const CHORES_EVENTS_ENABLED = true;

/**
 * Intents that render rich Chores & Events cards.
 */
export const CHORES_EVENTS_CARD_INTENTS = Object.freeze([
    'chore_form',
    'chore_created',
    'chore_failed',
    'view_chores',
    'chore_frequency_options',
    'chore_roommate_options',

    'event_flow',
    'event_preview',
    'event_created',
    'event_failed',
    'view_events_duplicate',
    'view_events',
    'event_detail',
    'event_guest_invite_prompt',
]);

/**
 * Text-only Chores & Events intents.
 */
export const CHORES_EVENTS_TEXT_INTENTS = Object.freeze([
    'chore_flow',
    'chore_cancelled',
    'view_chores_empty',

    'chore_status_update_flow',
    'chore_status_updated',
    'chore_status_update_failed',
    'chore_status_update_cancelled',

    'chore_deleted',
    'chore_delete_failed',

    'chore_duedate_prompt',

    'event_cancelled',
    'view_events_empty',
    'view_events_failed',
    'event_detail_failed',

    'event_guest_added',
    'event_guest_failed',
    'event_guest_done',

    'event_deleted',
    'event_delete_failed',

    'event_cancel_success',
    'event_cancel_failed',
]);

export const CHORES_EVENTS_ALL_INTENTS = Object.freeze([
    ...CHORES_EVENTS_CARD_INTENTS,
    ...CHORES_EVENTS_TEXT_INTENTS,
]);

/**
 * Outcome types that disable previous interactive cards.
 */
export const CHORES_EVENTS_OUTCOME_TYPES = Object.freeze([
    'chore_created',
    'chore_failed',
    'chore_deleted',
    'chore_delete_failed',

    'event_created',
    'event_failed',
    'event_deleted',
    'event_delete_failed',
    'event_cancel_success',
    'event_cancel_failed',
]);

export function isChoresEventsCardIntent(type) {
    return (
        CHORES_EVENTS_ENABLED &&
        CHORES_EVENTS_CARD_INTENTS.includes(type)
    );
}

export function isChoresEventsIntent(type) {
    return (
        CHORES_EVENTS_ENABLED &&
        CHORES_EVENTS_ALL_INTENTS.includes(type)
    );
}


/* =========================================================
   MANAGE GUEST GROUPS
   ========================================================= */

/**
 * Manage Guest Groups chatbot feature.
 */
export const MANAGE_GUEST_GROUPS_ENABLED = true;

/**
 * Rich Manage Guest Group UI intents.
 */
export const MANAGE_GUEST_GROUP_CARD_INTENTS = Object.freeze([
    'guest_group_flow',
    'guest_group_preview',
    'guest_group_created',
    'guest_group_failed',
    'view_guest_groups',
    'guest_group_detail',
]);

/**
 * Text-only Guest Group intents.
 */
export const MANAGE_GUEST_GROUP_TEXT_INTENTS = Object.freeze([
    'guest_group_cancelled',
    'view_guest_groups_empty',
    'view_guest_groups_failed',
    'guest_group_detail_failed',
    'guest_group_guest_delete_failed',
]);

export const MANAGE_GUEST_GROUP_ALL_INTENTS = Object.freeze([
    ...MANAGE_GUEST_GROUP_CARD_INTENTS,
    ...MANAGE_GUEST_GROUP_TEXT_INTENTS,
]);

/**
 * Completed / outcome intents.
 */
export const MANAGE_GUEST_GROUP_OUTCOME_TYPES = Object.freeze([
    'guest_group_created',
    'guest_group_failed',
    'guest_group_cancelled',
    'guest_group_guest_delete_failed',
]);

export function isManageGuestGroupCardIntent(type) {
    return (
        MANAGE_GUEST_GROUPS_ENABLED &&
        MANAGE_GUEST_GROUP_CARD_INTENTS.includes(type)
    );
}

export function isManageGuestGroupIntent(type) {
    return (
        MANAGE_GUEST_GROUPS_ENABLED &&
        MANAGE_GUEST_GROUP_ALL_INTENTS.includes(type)
    );
}


/* =========================================================
   NOTIFICATIONS
   ========================================================= */

/**
 * Notifications chatbot feature.
 */
export const NOTIFICATIONS_CHATBOT_ENABLED = true;

export const NOTIFICATION_CARD_INTENTS = Object.freeze([
    'view_notifications_list',
    'notification_marked_read',
]);

export const NOTIFICATION_TEXT_INTENTS = Object.freeze([
    'notification_flow',
    'view_notifications_empty',
    'view_notifications_failed',
    'notification_mark_read_failed',
]);

export const NOTIFICATION_ALL_INTENTS = Object.freeze([
    ...NOTIFICATION_CARD_INTENTS,
    ...NOTIFICATION_TEXT_INTENTS,
]);

export const NOTIFICATION_OUTCOME_TYPES = Object.freeze([
    'notification_marked_read',
    'notification_mark_read_failed',
]);

export function isNotificationCardIntent(type) {
    return (
        NOTIFICATIONS_CHATBOT_ENABLED &&
        NOTIFICATION_CARD_INTENTS.includes(type)
    );
}

export function isNotificationIntent(type) {
    return (
        NOTIFICATIONS_CHATBOT_ENABLED &&
        NOTIFICATION_ALL_INTENTS.includes(type)
    );
}


/* =========================================================
   ROOMMATE
   ========================================================= */

/**
 * Add Roommate chatbot feature.
 */
export const ROOMMATE_CHATBOT_ENABLED = true;

export const ROOMMATE_CARD_INTENTS = Object.freeze([
    'roommate_phone_prompt',
    'roommate_form',
    'roommate_preview',
    'roommate_created',
    'roommate_failed',
    'view_roommates',
]);

export const ROOMMATE_TEXT_INTENTS = Object.freeze([
    'roommate_flow',
    'roommate_cancelled',
    'view_roommates_empty',
    'view_roommates_failed',
    'roommate_deleted',
    'roommate_delete_failed',
]);

export const ROOMMATE_ALL_INTENTS = Object.freeze([
    ...ROOMMATE_CARD_INTENTS,
    ...ROOMMATE_TEXT_INTENTS,
]);

export const ROOMMATE_OUTCOME_TYPES = Object.freeze([
    'roommate_created',
    'roommate_failed',
    'roommate_cancelled',
    'roommate_deleted',
    'roommate_delete_failed',
]);

export function isRoommateCardIntent(type) {
    return (
        ROOMMATE_CHATBOT_ENABLED &&
        ROOMMATE_CARD_INTENTS.includes(type)
    );
}

export function isRoommateIntent(type) {
    return (
        ROOMMATE_CHATBOT_ENABLED &&
        ROOMMATE_ALL_INTENTS.includes(type)
    );
}


/* =========================================================
   VENDOR LIST
   Existing VendorChatbot.vue functionality
   ========================================================= */

/**
 * Vendor listing chatbot feature.
 */
export const VENDOR_CHATBOT_ENABLED = true;

/**
 * Rich VendorChatbot.vue intents.
 */
export const VENDOR_CARD_INTENTS = Object.freeze([
    'vendor_list',
]);

/**
 * Text-only Vendor listing intents.
 */
export const VENDOR_TEXT_INTENTS = Object.freeze([
    'vendor_list_failed',
    'vendor_status_updated',
    'vendor_status_update_failed',
]);

export const VENDOR_ALL_INTENTS = Object.freeze([
    ...VENDOR_CARD_INTENTS,
    ...VENDOR_TEXT_INTENTS,
]);

export const VENDOR_OUTCOME_TYPES = Object.freeze([
    'vendor_status_updated',
    'vendor_status_update_failed',
]);

export function isVendorCardIntent(type) {
    return (
        VENDOR_CHATBOT_ENABLED &&
        VENDOR_CARD_INTENTS.includes(type)
    );
}

export function isVendorIntent(type) {
    return (
        VENDOR_CHATBOT_ENABLED &&
        VENDOR_ALL_INTENTS.includes(type)
    );
}


/* =========================================================
   HOUSEHOLD EXPENSE
   ========================================================= */

/**
 * Household Expense chatbot feature.
 */
export const HOUSEHOLD_EXPENSE_ENABLED = true;

export const HOUSEHOLD_EXPENSE_CARD_INTENTS = Object.freeze([
    'household_expense_type_prompt',
    'household_expense_option_prompt',
    'household_expense_form',
    'household_expense_created',
    'household_expense_failed',
]);

export const HOUSEHOLD_EXPENSE_TEXT_INTENTS = Object.freeze([
    'household_expense_flow',
    'household_expense_cancelled',
]);

export const HOUSEHOLD_EXPENSE_ALL_INTENTS = Object.freeze([
    ...HOUSEHOLD_EXPENSE_CARD_INTENTS,
    ...HOUSEHOLD_EXPENSE_TEXT_INTENTS,
]);

export const HOUSEHOLD_EXPENSE_OUTCOME_TYPES = Object.freeze([
    'household_expense_created',
    'household_expense_failed',
    'household_expense_cancelled',
]);

export function isHouseholdExpenseCardIntent(type) {
    return (
        HOUSEHOLD_EXPENSE_ENABLED &&
        HOUSEHOLD_EXPENSE_CARD_INTENTS.includes(type)
    );
}

export function isHouseholdExpenseIntent(type) {
    return (
        HOUSEHOLD_EXPENSE_ENABLED &&
        HOUSEHOLD_EXPENSE_ALL_INTENTS.includes(type)
    );
}


/* =========================================================
   MANAGE VENDORS
   Create Vendor flow
   ========================================================= */

/**
 * Manage Vendors chatbot feature.
 *
 * IMPORTANT:
 * vendor_flow is intentionally TEXT intent.
 *
 * Company Name / Email / normal questions:
 *     vendor_flow -> normal chatbot message.
 *
 * Contact Number question:
 *     vendor_flow -> chatbot.vue detects phone prompt
 *                 -> ManageVendors.vue rich phone UI.
 *
 * Preview / Created / Failed:
 *     ManageVendors.vue rich UI.
 */
export const MANAGE_VENDORS_ENABLED = true;

/**
 * Rich ManageVendors.vue intents.
 *
 * DO NOT put vendor_flow here.
 */
export const MANAGE_VENDOR_CARD_INTENTS = Object.freeze([
    'vendor_preview',
    'vendor_created',
    'vendor_failed',
]);

/**
 * Normal text Manage Vendor intents.
 *
 * vendor_flow remains text because several different questions
 * use the same backend intent.
 */
export const MANAGE_VENDOR_TEXT_INTENTS = Object.freeze([
    'vendor_flow',
    'vendor_cancelled',
]);

/**
 * All Manage Vendor intents.
 */
export const MANAGE_VENDOR_ALL_INTENTS = Object.freeze([
    ...MANAGE_VENDOR_CARD_INTENTS,
    ...MANAGE_VENDOR_TEXT_INTENTS,
]);

/**
 * Completed / error outcomes.
 */
export const MANAGE_VENDOR_OUTCOME_TYPES = Object.freeze([
    'vendor_created',
    'vendor_failed',
    'vendor_cancelled',
]);

/**
 * Rich Manage Vendor UI checker.
 */
export function isManageVendorCardIntent(type) {
    return (
        MANAGE_VENDORS_ENABLED &&
        MANAGE_VENDOR_CARD_INTENTS.includes(type)
    );
}

/**
 * Any Manage Vendor flow checker.
 */
export function isManageVendorIntent(type) {
    return (
        MANAGE_VENDORS_ENABLED &&
        MANAGE_VENDOR_ALL_INTENTS.includes(type)
    );
}